import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, AlertCircle, CheckCircle, Ticket, Tag, Building2, Mail, Phone, FileText, Send, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';

interface RaiseTicketProps {
  clientId: string;
  token: string;
  userEmail?: string;
  companyName?: string;
}

interface TicketFormData {
  title: string;
  description: string;
  queryName: string;
  companyName: string;
  emailAddress: string;
  phoneNumber: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
}

interface FileAttachment {
  file: File;
  preview?: string;
}

const PRIORITY_CONFIG = {
  low:      { label: 'Low',      color: 'text-blue-500',   bg: 'bg-blue-500/10 border-blue-500/30'   },
  medium:   { label: 'Medium',   color: 'text-yellow-500', bg: 'bg-yellow-500/10 border-yellow-500/30' },
  high:     { label: 'High',     color: 'text-orange-500', bg: 'bg-orange-500/10 border-orange-500/30' },
  critical: { label: 'Critical', color: 'text-red-500',    bg: 'bg-red-500/10 border-red-500/30'    },
};

const CATEGORIES = ['General', 'Technical', 'Billing', 'Feature Request', 'Bug Report', 'Hardware', 'Network', 'Other'];

export default function RaiseTicket({ clientId, token, userEmail = '', companyName = '' }: RaiseTicketProps) {
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    description: '',
    queryName: '',
    companyName,
    emailAddress: userEmail,
    phoneNumber: '',
    priority: 'medium',
    category: 'General',
  });

  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const processFiles = (files: FileList) => {
    const newFiles = Array.from(files);
    if (attachments.length + newFiles.length > 5) {
      setError('Maximum 5 files allowed');
      return;
    }
    const allowed = ['image/jpeg','image/png','image/gif','image/webp','application/pdf','application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain','text/csv'];
    for (const file of newFiles) {
      if (!allowed.includes(file.type)) { setError(`File type not allowed: ${file.name}`); return; }
      if (file.size > 50 * 1024 * 1024) { setError(`File too large: ${file.name} (max 50MB)`); return; }
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setAttachments(prev => prev.map(a => a.file === file ? { ...a, preview: ev.target?.result as string } : a));
        reader.readAsDataURL(file);
      }
      setAttachments(prev => [...prev, { file }]);
    }
    setError('');
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) processFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.emailAddress) {
      setError('Title and email address are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) => payload.append(k, v));
      attachments.forEach(a => payload.append('attachments', a.file));

      const res = await fetch(`/api/clients/${clientId}/tickets`, {
        method: 'POST',
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
        body: payload,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create ticket');

      setSuccess(true);
      setCreatedTicketId(data.ticket?.id || '');
      setFormData({ title: '', description: '', queryName: '', companyName, emailAddress: userEmail, phoneNumber: '', priority: 'medium', category: 'General' });
      setAttachments([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-sm";
  const labelClass = "block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5";

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Ticket className="h-5 w-5 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Raise a Support Ticket</h1>
        </div>
        <p className="text-muted-foreground text-sm ml-12">Describe your issue and our team will respond promptly.</p>
      </motion.div>

      {/* Success Banner */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3"
          >
            <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-600 dark:text-emerald-400">Ticket Created Successfully!</p>
              {createdTicketId && (
                <p className="text-sm text-muted-foreground mt-1">
                  Ticket ID: <code className="font-mono bg-muted px-2 py-0.5 rounded text-xs">{createdTicketId}</code>
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-1">Check "My Tickets" to track your request.</p>
            </div>
            <button onClick={() => setSuccess(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3"
          >
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400 flex-1">{error}</p>
            <button onClick={() => setError('')} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title + Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}><FileText className="inline h-3 w-3 mr-1" />Ticket Title *</label>
              <input name="title" value={formData.title} onChange={handleInputChange} placeholder="Brief description of your issue" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Priority</label>
              <div className="flex gap-2 flex-wrap">
                {(Object.keys(PRIORITY_CONFIG) as Array<keyof typeof PRIORITY_CONFIG>).map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                      formData.priority === p
                        ? `${PRIORITY_CONFIG[p].bg} ${PRIORITY_CONFIG[p].color} border-current`
                        : 'border-border text-muted-foreground hover:border-muted-foreground bg-background'
                    }`}
                  >
                    {PRIORITY_CONFIG[p].label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category + Query Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}><Tag className="inline h-3 w-3 mr-1" />Category</label>
              <select name="category" value={formData.category} onChange={handleInputChange} className={inputClass}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Query Name</label>
              <input name="queryName" value={formData.queryName} onChange={handleInputChange} placeholder="Specific issue or feature name" className={inputClass} />
            </div>
          </div>

          {/* Company + Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}><Building2 className="inline h-3 w-3 mr-1" />Company Name</label>
              <input name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="Your company name" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}><Mail className="inline h-3 w-3 mr-1" />Email Address *</label>
              <input type="email" name="emailAddress" value={formData.emailAddress} onChange={handleInputChange} placeholder="your@email.com" className={inputClass} required />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className={labelClass}><Phone className="inline h-3 w-3 mr-1" />Phone Number</label>
            <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder="+91 98765 43210" className={inputClass} />
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>Detailed Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Please provide detailed information about your issue, steps to reproduce, expected vs actual behavior..."
              rows={5}
              className={`${inputClass} resize-vertical`}
            />
          </div>

          {/* File Upload */}
          <div>
            <label className={labelClass}><Upload className="inline h-3 w-3 mr-1" />Attachments <span className="normal-case font-normal">(optional, max 5 files · 50MB each)</span></label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-border hover:border-blue-500/50 hover:bg-accent/50'
              }`}
            >
              <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm font-medium text-foreground">Drag & drop or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">Images, PDF, Word, Excel, Text supported</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={e => e.target.files && processFiles(e.target.files)}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />
            </div>

            {attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Attached Files ({attachments.length}/5)</p>
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 bg-muted/50 border border-border rounded-lg p-3">
                    {a.preview
                      ? <img src={a.preview} alt={a.file.name} className="w-10 h-10 object-cover rounded-md shrink-0" />
                      : <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-muted-foreground" /></div>
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{a.file.name}</p>
                      <p className="text-xs text-muted-foreground">{(a.file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button type="button" onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-border">
            <Button type="submit" disabled={loading} className="flex-1 gap-2">
              {loading ? <><div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating...</> : <><Send className="h-4 w-4" />Create Ticket</>}
            </Button>
            <Button type="button" variant="outline" className="gap-2" onClick={() => {
              setFormData({ title: '', description: '', queryName: '', companyName, emailAddress: userEmail, phoneNumber: '', priority: 'medium', category: 'General' });
              setAttachments([]);
              setError('');
            }}>
              <RotateCcw className="h-4 w-4" />Clear
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
